// src/oura/components/ReplyPictures.tsx
//
// Pictures on a reply to a client's request: staff add screenshots of the
// finished work, the client sees them under the reply.
//
//   - ReplyPicturePicker: the staff side. "Add screenshots" picks up to
//     REPLY_PICTURES_MAX images (PNG, JPEG, WebP); a screenshot pasted or dropped on the
//     reply field lands here too (usePictureInput). Each shows as a small tile
//     with a remove button, before anything is uploaded.
//   - ReplyPictureGrid: both sides. The reply's pictures as tiles; a tile
//     opens the picture full size in a viewer (arrows walk them, Escape
//     closes). Anything that is not an image is a chip that opens in a new tab.
//
// Off in a build until the reply route takes `fileKeys` (REPLY_PICTURES_ON):
// sending pictures to a server that drops them would lose them silently.

import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent, type DragEvent } from "react";
import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon, FileTextIcon, ImagePlusIcon, XIcon } from "lucide-react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn";
import { FOCUS_RING, PillButton, TYPE } from "@oura/ui";
import { useWritesPaused } from "@context/maintenance";

export const REPLY_PICTURES_ON = import.meta.env.VITE_ENABLE_OURA_REPLY_PICTURES === "true";
export const REPLY_PICTURES_MAX = 6;
// The change-request upload route's own limits (POST /change-requests/uploads).
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = ".png,.jpg,.jpeg,.webp";
const OK_TYPE = /^image\/(png|jpe?g|webp)$/i;

export interface ReplyPicture {
    name: string;
    url: string;
}

const isPdf = (name: string) => /\.pdf$/i.test(name);

/* ------------------------------------------------------------------ */
/* Staff: picking                                                      */
/* ------------------------------------------------------------------ */

/**
 * Adds files to the list, checked: PNG, JPEG or WebP, 10 MB each, at most
 * REPLY_PICTURES_MAX. Returns the sentence for the first thing refused.
 */
export function addPictures(current: File[], incoming: File[]): { files: File[]; error: string | null } {
    const files = [...current];
    let error: string | null = null;
    for (const f of incoming) {
        if (!OK_TYPE.test(f.type) && !/\.(png|jpe?g|webp)$/i.test(f.name)) {
            error ??= `${f.name || "That file"} is not a picture. Use PNG, JPEG or WebP.`;
            continue;
        }
        if (f.size > MAX_BYTES) {
            error ??= `${f.name || "That file"} is over 10 MB.`;
            continue;
        }
        if (files.length >= REPLY_PICTURES_MAX) {
            error ??= `Up to ${REPLY_PICTURES_MAX} screenshots on one reply.`;
            break;
        }
        // A pasted screenshot comes in as "image.png": name it so the client's list reads.
        const named = /^image\.(png|jpe?g)$/i.test(f.name) ? new File([f], `Screenshot ${files.length + 1}.${f.name.split(".").pop()}`, { type: f.type }) : f;
        files.push(named);
    }
    return { files, error };
}

/** Paste and drop on the reply field: screenshots land in the list, text pastes as usual. */
export function usePictureInput(files: File[], onFiles: (files: File[]) => void, onError: (text: string) => void, enabled: boolean) {
    const [over, setOver] = useState(false);
    const take = useCallback(
        (incoming: File[]) => {
            const res = addPictures(files, incoming);
            onFiles(res.files);
            if (res.error) onError(res.error);
        },
        [files, onFiles, onError],
    );
    if (!enabled) return { over: false, handlers: {} };
    return {
        over,
        handlers: {
            onPaste: (e: ClipboardEvent<HTMLTextAreaElement>) => {
                const pics = Array.from(e.clipboardData?.files ?? []);
                if (pics.length === 0) return;
                e.preventDefault();
                take(pics);
            },
            onDragOver: (e: DragEvent<HTMLElement>) => {
                if (!Array.from(e.dataTransfer.types).includes("Files")) return;
                e.preventDefault();
                setOver(true);
            },
            onDragLeave: () => setOver(false),
            onDrop: (e: DragEvent<HTMLElement>) => {
                const pics = Array.from(e.dataTransfer.files ?? []);
                setOver(false);
                if (pics.length === 0) return;
                e.preventDefault();
                take(pics);
            },
        },
    };
}

/** One local file's preview link, revoked when the tile goes. */
function useObjectUrl(file: File) {
    const url = useMemo(() => URL.createObjectURL(file), [file]);
    useEffect(() => () => URL.revokeObjectURL(url), [url]);
    return url;
}

function PickedTile({ file, onRemove, disabled }: { file: File; onRemove: () => void; disabled?: boolean }) {
    const url = useObjectUrl(file);
    const pdf = isPdf(file.name) || file.type === "application/pdf";
    return (
        <li className="relative h-16 w-20 shrink-0 overflow-hidden rounded-lg bg-gray-100 ring-1 ring-black/[0.06]" title={file.name}>
            {pdf ? (
                <span className="flex h-full w-full flex-col items-center justify-center gap-1 px-1 text-center">
                    <FileTextIcon aria-hidden size={18} className="text-red-600" />
                    <span className={cn(TYPE.tm, "w-full truncate text-gray-600")}>{file.name}</span>
                </span>
            ) : (
                <img src={url} alt={file.name} className="h-full w-full object-cover" />
            )}
            <button
                type="button"
                disabled={disabled}
                onClick={onRemove}
                aria-label={`Remove ${file.name}`}
                className={cn("absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-gray-900/70 text-white hover:bg-gray-900 disabled:opacity-50", FOCUS_RING)}
            >
                <XIcon aria-hidden size={11} strokeWidth={2.5} />
            </button>
        </li>
    );
}

/**
 * The staff side, under the reply field: the tiles picked so far and the
 * "Add screenshots" button. Nothing uploads until Send reply.
 */
export function ReplyPicturePicker({
    files,
    onFiles,
    onError,
    disabled,
}: {
    files: File[];
    onFiles: (files: File[]) => void;
    onError: (text: string) => void;
    disabled?: boolean;
}) {
    const writesPaused = useWritesPaused();
    const input = useRef<HTMLInputElement>(null);
    const full = files.length >= REPLY_PICTURES_MAX;
    return (
        <div className="mt-2">
            <input
                ref={input}
                type="file"
                hidden
                multiple
                disabled={disabled || writesPaused}
                accept={ACCEPT}
                onChange={(e) => {
                    const picked = Array.from(e.target.files ?? []);
                    e.target.value = "";
                    if (picked.length === 0) return;
                    const res = addPictures(files, picked);
                    onFiles(res.files);
                    if (res.error) onError(res.error);
                }}
            />
            {files.length > 0 && (
                <ul aria-label="Screenshots on this reply" className="list-plain m-0 mb-2 flex flex-wrap gap-2 p-0">
                    {files.map((f, i) => (
                        <PickedTile key={`${f.name}-${f.size}-${i}`} file={f} disabled={disabled} onRemove={() => onFiles(files.filter((_, j) => j !== i))} />
                    ))}
                </ul>
            )}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {!full && (
                    <PillButton variant="text" icon={ImagePlusIcon} disabled={disabled || writesPaused} onClick={() => input.current?.click()}>
                        {files.length > 0 ? "Add more" : "Add screenshots"}
                    </PillButton>
                )}
                <span className={cn(TYPE.tc, "text-gray-500")}>
                    {files.length > 0 ? `${files.length} of ${REPLY_PICTURES_MAX}. The client sees them under your reply.` : "Or paste a screenshot into the reply."}
                </span>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Both sides: showing                                                 */
/* ------------------------------------------------------------------ */

/**
 * A reply's pictures. Images as tiles that open full size here; PDFs as
 * chips that open in a new tab. `size` "sm" fits a list row.
 */
export function ReplyPictureGrid({ files, size = "md", className }: { files: ReplyPicture[] | null | undefined; size?: "sm" | "md"; className?: string }) {
    const [open, setOpen] = useState<number | null>(null);
    const list = (files ?? []).filter((f) => f && f.name);
    const images = list.filter((f) => !isPdf(f.name) && f.url);
    const docs = list.filter((f) => isPdf(f.name) || !f.url);
    if (list.length === 0) return null;
    const tile = size === "sm" ? "h-16 w-24" : images.length === 1 ? "aspect-[16/10] w-full max-w-[22rem]" : "aspect-[4/3] w-full";
    return (
        <div className={cn("mt-3", className)}>
            {images.length > 0 && (
                <ul
                    aria-label={images.length === 1 ? "1 screenshot" : `${images.length} screenshots`}
                    className={cn("list-plain m-0 p-0", size === "sm" || images.length === 1 ? "flex flex-wrap gap-2" : "grid grid-cols-2 gap-2 sm:grid-cols-3")}
                >
                    {images.map((f, i) => (
                        <li key={`${f.url}-${i}`} className={cn("min-w-0", size === "sm" && "shrink-0")}>
                            <button
                                type="button"
                                onClick={() => setOpen(i)}
                                title={f.name}
                                className={cn(
                                    tile,
                                    "group relative block overflow-hidden rounded-xl bg-gray-100 ring-1 ring-black/[0.06] transition hover:ring-blue-600/40",
                                    FOCUS_RING,
                                )}
                            >
                                <img src={f.url} alt={f.name} loading="lazy" className="h-full w-full object-cover object-left-top transition group-hover:scale-[1.02]" />
                                <span className="sr-only">Open {f.name} full size</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {docs.length > 0 && (
                <ul aria-label="Files" className="list-plain m-0 mt-2 flex flex-wrap gap-2 p-0">
                    {docs.map((f, i) => (
                        <li key={`${f.name}-${i}`} className="min-w-0 max-w-full">
                            <a
                                href={f.url || undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cn(TYPE.tc, "inline-flex min-h-8 max-w-full items-center gap-1.5 rounded-full bg-gray-50 px-3 font-medium text-blue-600 hover:bg-gray-100", FOCUS_RING)}
                            >
                                <FileTextIcon aria-hidden size={14} className="shrink-0 text-red-600" />
                                <span className="truncate">{f.name}</span>
                                <span className="sr-only">(opens in a new tab)</span>
                            </a>
                        </li>
                    ))}
                </ul>
            )}
            {open !== null && images[open] && <PictureViewer images={images} at={open} onMove={setOpen} onClose={() => setOpen(null)} />}
        </div>
    );
}

/** One picture full size over the page. Arrows walk them, Escape or the backdrop closes. */
function PictureViewer({ images, at, onMove, onClose }: { images: ReplyPicture[]; at: number; onMove: (i: number) => void; onClose: () => void }) {
    const closeRef = useRef<HTMLButtonElement>(null);
    const many = images.length > 1;
    const f = images[at];
    useEffect(() => {
        const back = document.activeElement as HTMLElement | null;
        closeRef.current?.focus();
        return () => back?.focus?.();
    }, []);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                onClose();
            } else if (many && e.key === "ArrowRight") onMove((at + 1) % images.length);
            else if (many && e.key === "ArrowLeft") onMove((at - 1 + images.length) % images.length);
        };
        // Capture: the drawer under this closes on Escape too, and must not.
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [at, images.length, many, onClose, onMove]);
    const arrow = cn("grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/90 text-gray-900 shadow hover:bg-white", FOCUS_RING);
    return createPortal(
        <div role="dialog" aria-modal="true" aria-label={f.name} className="fixed inset-0 z-[1000] flex flex-col bg-gray-950/85 p-4 backdrop-blur-sm" onClick={onClose}>
            <div className="flex items-center gap-3 text-white" onClick={(e) => e.stopPropagation()}>
                <p className={cn(TYPE.tb, "m-0 min-w-0 flex-1 truncate font-medium")}>
                    {f.name}
                    {many && <span className="ml-2 font-normal text-white/60 tabular-nums">{at + 1} of {images.length}</span>}
                </p>
                <a href={f.url} target="_blank" rel="noopener noreferrer" className={cn(TYPE.tc, "inline-flex items-center gap-1 rounded font-medium text-white/90 hover:text-white hover:underline", FOCUS_RING)}>
                    Open full size <ExternalLinkIcon aria-hidden size={12} />
                </a>
                <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className={cn("grid h-9 w-9 place-items-center rounded-full bg-white/10 hover:bg-white/20", FOCUS_RING)}>
                    <XIcon aria-hidden size={16} />
                </button>
            </div>
            <div className="flex min-h-0 flex-1 items-center justify-center gap-3 pt-4">
                {many && (
                    <button type="button" aria-label="Previous" className={arrow} onClick={(e) => (e.stopPropagation(), onMove((at - 1 + images.length) % images.length))}>
                        <ChevronLeftIcon aria-hidden size={18} />
                    </button>
                )}
                <img src={f.url} alt={f.name} onClick={(e) => e.stopPropagation()} className="max-h-full min-w-0 max-w-full rounded-lg bg-white object-contain shadow-2xl" />
                {many && (
                    <button type="button" aria-label="Next" className={arrow} onClick={(e) => (e.stopPropagation(), onMove((at + 1) % images.length))}>
                        <ChevronRightIcon aria-hidden size={18} />
                    </button>
                )}
            </div>
        </div>,
        document.body,
    );
}

/** "2 screenshots", for a one-line summary of a reply. */
export function picturesWord(n: number) {
    return n === 1 ? "1 screenshot" : `${n} screenshots`;
}
